import { z } from 'zod';
import { UserTableConfig } from '@rossum/api-client/shared';
import { User as ApiClientUser } from '@rossum/api-client/users';
import { defaultWorkspaceSorting, ThemeType } from '../constants/values';
import {
  DeprecatedSurveySteps,
  SurveyQuestionKeys,
  SurveySteps,
} from '../containers/TrialOnboarding/surveyData';
import {
  sidebarAdditionalInfoTypes,
  sidebarNavigationOptions,
} from '../features/annotation-view/document-sidebar/line-item-settings/formValues';
import { SidebarFooterActionsOrder } from '../features/annotation-view/document-sidebar/sidebar-footer/sidebar-footer-actions/utils';
import { FeatureId } from '../features/feature-enablement/features';
import { PASSWORD, SSO } from '../redux/modules/users/values';
import { GroupRole } from './group';
import { UserProductToursSummary } from './productTour';
import { UiFieldType } from './schema';

// deprecated - keeping just in case for now
type Hints = {
  firstAnnotation?: boolean;
  firstDatapoint?: boolean;
  firstDownload?: boolean;
  firstExport?: boolean;
  firstUpload?: boolean;
  hubspotForm?: boolean;
  tourSkipped?: boolean;
  visitExported?: boolean;
};

export const sortingValues = [
  defaultWorkspaceSorting,
  'alphabeticalDesc',
  'newest',
  'oldest',
  'toReview',
] as const;

export type SortingValue = (typeof sortingValues)[number];

type DataCaptureSurveyData = {
  viewCount: number | null;
  skipCount: number | null;
  answer: number | null;
  annotationIds: number[] | null;
  trigger: string | null;
  lastModifiedAt: string | null;
};

export type AnnotationValidation = 'automatically' | 'on-demand';
export type FooterLayout = 'table-per-page' | 'one-table';
export type SortFooterColumns = 'automatically' | 'queue-settings';

type SentInvitation = {
  email: string;
  jobPosition: string;
  dateJoined: string | undefined;
  dateOfInvitation: string;
};

const formulaPreviewWidths = z.record(z.number());

export const formulaPreviewSchema = z.object({
  widths: formulaPreviewWidths,
});

type FormulaPreview = z.infer<typeof formulaPreviewSchema>;

export type LabFeatureConfig = {
  enabled: boolean;
  lastUpdatedAt: string;
  sentFeedback?: boolean;
};

// This is what will live inside UI settings of users
export type RossumLabConfig = Partial<
  Record<FeatureId, LabFeatureConfig | undefined>
>;

export type UiSettings = {
  anyDatapointCorrected?: boolean;
  dashboard: {
    workspacesSorting?: SortingValue;
    hiddenHints?: string[];
  };
  documentsExported?: number;
  hideExtensionsIntroBanner?: boolean;
  hideDagIntroBanner?: boolean;
  hideWarningEditableFields?: Partial<
    Record<Extract<UiFieldType, 'captured' | 'data'>, boolean>
  >;
  hints?: Hints;
  isWizardSubmitted?: boolean;
  /**
   * User locale preference. `null` means "no explicit preference" —
   * the FE then falls back to browser locale / fallback. Send `null`
   * over the wire to clear a previously saved preference.
   */
  locale?: string | null;
  onboardingAcknowledged?: boolean;
  theme?: ThemeType;
  productTours?: UserProductToursSummary;
  onboardingSurvey?: {
    activeStep: SurveyQuestionKeys | DeprecatedSurveySteps;
    stepsData: Partial<SurveySteps>;
  };
  feedbackFormsSubmitted?: Record<string, boolean>;
  dataCaptureSurveyData?: Partial<DataCaptureSurveyData>;
  footerLayout?: FooterLayout;
  sortFooterColumns?: SortFooterColumns;
  auroraTourCompleted?: boolean;
  auroraPromoCompleted?: boolean;
  complexLineItems?: boolean;
  sentInvitations?: Array<SentInvitation>;
  documentsDashboard?: UserTableConfig;
  annotationValidation?: AnnotationValidation;
  automaticSuggestions?: boolean;
  formulaPreview?: FormulaPreview;
  showConfidenceScore?: boolean;
  sidebarAdditionalInfo?: Array<(typeof sidebarAdditionalInfoTypes)[number]>;
  sidebarActionsOrder?: SidebarFooterActionsOrder;
  sidebarNavigation?: { onConfirm: (typeof sidebarNavigationOptions)[number] };

  labConfig?: RossumLabConfig;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  hookState?: Record<string, any>;
};

export type AuthType = typeof SSO | typeof PASSWORD;

export type User = Omit<ApiClientUser, 'uiSettings'> & {
  uiSettings: UiSettings;
};

export type OrganizationUser = User & {
  queuesCount: number;
  role: GroupRole;
  workspacesCount: number;
};

export type UserList = Array<User>;
